import React, { useState } from 'react';
import { Box, CloseButton, Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import { pxToRem, styled } from '@withjoy/joykit/theme';
import { useOuterClick } from '@shared/utils/hooks/useOuterClick';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { UserConsentKey } from '@shared/utils/userConsent';
import { useUserConsent } from '@shared/utils/userConsent';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useTimeout } from '@shared/hooks/useTimeout';

const TOOLTIP_WIDTH = pxToRem(256);

// Hides until popper.js sets `data-popper-placement`, avoiding a top-left flash on first paint.
// 300ms keyframe fallback unhides the popover if the attribute is ever not set (defensive — popper.js
// reliably sets it today, but a future popper change would otherwise leave the tooltip permanently hidden).
const PositionGatedRoot = styled(Box)`
  visibility: hidden;
  animation: info-tooltip-position-fallback 0s linear 300ms forwards;
  @keyframes info-tooltip-position-fallback {
    to {
      visibility: visible;
    }
  }
  &[data-popper-placement] {
    visibility: visible;
    animation: none;
  }
`;

type TooltipDescriptionPair = Readonly<{
  ariaLabel: string;
  description: string;
}>;

interface InfoTooltipProps
  extends Readonly<{
    title?: string;
    ariaLabel?: string;
    tooltip?: TooltipDescriptionPair;
    /**
     * @param content - Custom content to render in the tooltip (preferred for complex layouts with custom components)
     * @param description - Simple text description (legacy prop; prefer `tooltip` for new implementations so ariaLabel is required)
     */
    description?: string;
    content?: React.ReactNode;
    children: React.ReactNode;
    autoOpen?: boolean;
    userConsentKey?: UserConsentKey;
    placement?: 'top' | 'bottom' | 'left' | 'right';
    tooltipOverrides?: React.ComponentProps<typeof TooltipV2>['overrides'];
    /** Renders the tooltip popper in a portal so it escapes any ancestor `overflow: hidden`.
     *  Defaults to `true` — turn off only when the tooltip must stay constrained to its parent
     *  (e.g. inside a layout that intentionally clips). */
    usePortal?: boolean;
  }> {}

const TOOLTIP_OVERRIDES = {
  Root: { component: PositionGatedRoot, props: { zIndex: 'overlay' } },
  Content: {
    props: {
      color: 'white',
      borderRadius: 3,
      padding: pxToRem(24),
      width: TOOLTIP_WIDTH,
    },
  },
  Arrow: { props: { backgroundColor: 'black' } },
} as const;

export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  title,
  content,
  tooltip,
  children,
  ariaLabel,
  description,
  userConsentKey,
  autoOpen = false,
  placement = 'right',
  tooltipOverrides,
  usePortal = true,
}) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const { getUserConsent, setUserConsent, loadingUserConsent } = useUserConsent();
  const isMobileOrTablet = useIsMobileOrTablet();
  const { set: setTimeout, clear: clearTimeout } = useTimeout();

  const descriptionText = tooltip?.description ?? description;
  const ariaLabelText = tooltip?.ariaLabel ?? ariaLabel ?? title ?? '';

  const hasSeenTooltip = userConsentKey ? getUserConsent(userConsentKey) : true;
  const shouldAutoOpen = autoOpen && !loadingUserConsent && !hasSeenTooltip;

  const handleClose = useEventCallback(() => {
    setTooltipVisible(false);
    if (userConsentKey) {
      setUserConsent(userConsentKey, true);
    }
  });

  const tooltipRef = useOuterClick(handleClose, tooltipVisible && shouldAutoOpen);

  const mergeOverrideKey = (key: keyof typeof TOOLTIP_OVERRIDES) => ({
    ...TOOLTIP_OVERRIDES[key],
    ...tooltipOverrides?.[key],
    props: {
      ...TOOLTIP_OVERRIDES[key].props,
      ...tooltipOverrides?.[key]?.props,
    },
  });

  const mergedTooltipOverrides: React.ComponentProps<typeof TooltipV2>['overrides'] = {
    ...TOOLTIP_OVERRIDES,
    ...tooltipOverrides,
    Root: mergeOverrideKey('Root'),
    Content: mergeOverrideKey('Content'),
    Arrow: mergeOverrideKey('Arrow'),
  };

  React.useEffect(() => {
    if (!shouldAutoOpen) return undefined;
    setTimeout(() => setTooltipVisible(true), 300);
    return () => clearTimeout();
  }, [shouldAutoOpen, setTimeout, clearTimeout]);

  const tooltipContent = (
    <Flex
      gap={3}
      flexDirection="column"
      ref={shouldAutoOpen ? tooltipRef : undefined}
    >
      {shouldAutoOpen && (
        <CloseButton
          onClick={handleClose}
          aria-label="Close tooltip"
          overrides={{
            Root: {
              props: {
                position: 'absolute',
                color: 'white',
                top: pxToRem(-30),
                right: pxToRem(-40),
                _hover: { color: 'white' },
                _active: { color: 'white' },
              },
            },
          }}
        />
      )}
      {content ? (
        content
      ) : (
        <>
          {title && (
            <TextV2
              typographyVariant="label3"
              fontWeight={600}
            >
              {title}
            </TextV2>
          )}
          {descriptionText && <Box typographyVariant="label2">{descriptionText}</Box>}
        </>
      )}
    </Flex>
  );

  return (
    <TooltipV2
      isOpen={shouldAutoOpen ? tooltipVisible : undefined}
      overrides={mergedTooltipOverrides}
      aria-label={ariaLabelText}
      placement={isMobileOrTablet ? 'bottom' : placement}
      content={tooltipContent}
      triggerType="hover"
      onMouseEnterDelay={1000}
      usePortal={usePortal}
    >
      {children}
    </TooltipV2>
  );
};
